import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Indigo-Deep–skinned modal shell for the operator console (Phase C-3).
 * Separate from the tenant-side ReasonModal (which must NOT be restyled — it's
 * shared with tenant payroll pages). Provides Escape-to-close, initial focus on
 * the dialog, focus restoration on unmount, aria-modal, and optional
 * backdrop-dismiss (default off for the mutation modals, which are
 * consequential and should require an explicit Cancel/Submit).
 *
 * Note: this is initial-focus + Escape + restore, not a full focus trap — the
 * codebase's hand-rolled modal posture (no Radix/Headless). Adequate for an
 * operator-only surface; promote to a library if the modal count grows.
 */
export function PlatformModal({
  open,
  onClose,
  title,
  children,
  footer,
  dismissOnBackdrop = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  dismissOnBackdrop?: boolean;
}): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement;
    dialogRef.current?.focus();
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (previouslyFocused.current instanceof HTMLElement) {
        previouslyFocused.current.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="platform-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={dismissOnBackdrop ? onClose : undefined}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="bg-white rounded-lg max-w-lg w-full mx-4 border-2 border-indigo-deep shadow-xl outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="bg-indigo-deep text-white px-6 py-4 rounded-t-lg">
          <h2 id="platform-modal-title" className="text-lg font-semibold">
            {title}
          </h2>
        </header>
        <div className="px-6 py-4 text-gray-900">{children}</div>
        {footer ? (
          <footer className="px-6 py-3 border-t bg-gray-50 rounded-b-lg flex justify-end gap-2">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
